import * as React from 'react'
import {
	getHighlightForToken,
	type DocumentPlaceholder,
} from '#app/services/document.ts'

type RenderDocumentProps = {
	content: string
	placeholders: DocumentPlaceholder[]
}

export function RenderDocument(props: RenderDocumentProps) {
	const placeholderIndexRef = React.useRef(0)
	// do this so the ref doesn't get incremented again on rerenders
	placeholderIndexRef.current = 0

	function getNextPlaceholder() {
		return props.placeholders[placeholderIndexRef.current++]?.slug ?? ''
	}

	let chunks = props.content.split('\n').filter(x => x.trim() !== '')

	return (
		<div className="text-sm">
			{chunks.map((paragraph, index) => (
				<p className="py-1" key={index}>
					<RenderParagraph
						content={paragraph}
						getNextPlaceholder={getNextPlaceholder}
					/>
				</p>
			))}
		</div>
	)
}

function RenderParagraph(props: {
	content: string
	getNextPlaceholder: () => string
}) {
	const chunksClassName = 'italic underline document-token'
	let nodes: React.ReactNode[] = []

	let localChunk: string[] = []

	props.content.split(' ').forEach((word, index) => {
		let highlightForWord = getHighlightForToken(word)
		if (highlightForWord) {
			if (highlightForWord !== null) {
				nodes.push(
					<React.Fragment key={word + index + 'highlight'}>
						<HighlightElementForToken
							token={word}
							highlight={highlightForWord}
						/>{' '}
					</React.Fragment>,
				)
			}
		} else if (word.startsWith('[')) {
			// highlight as cursive multiple words like [Nombre y apellidos]
			if (word.includes(']')) {
				let wordUntilClosingBracket = word.split(']')[0]
				let wordsAfterClosingBracket = word.split(']').slice(1).join(' ')
				localChunk.push(wordUntilClosingBracket + ']')
				let placeholder = props.getNextPlaceholder()
				nodes.push(
					<React.Fragment key={word + index + placeholder + 'start'}>
						<span
							data-field-placeholder={placeholder}
							className={chunksClassName}
						>
							{localChunk.join(' ')}
						</span>
						{wordsAfterClosingBracket}{' '}
					</React.Fragment>,
				)
				localChunk = []
			} else {
				localChunk.push(word)
			}
		} else if (word.includes(']')) {
			let wordUntilClosingBracket = word.split(']')[0]
			let wordsAfterClosingBracket = word.split(']').slice(1).join(' ')
			localChunk.push(wordUntilClosingBracket + ']')
			let placeholder = props.getNextPlaceholder()
			nodes.push(
				<React.Fragment key={word + index + placeholder + 'start'}>
					<span
						data-field-placeholder={placeholder}
						className={chunksClassName}
						key={word + index + placeholder + 'end'}
					>
						{localChunk.join(' ')}
					</span>
					{wordsAfterClosingBracket}{' '}
				</React.Fragment>,
			)
			localChunk = []
		} else if (localChunk.length > 0) {
			localChunk.push(word)
		} else {
			nodes.push(<span key={word + index + 'node'}>{word} </span>)
		}
	})

	return <>{nodes}</>
}

function HighlightElementForToken(props: {
	token: string
	highlight: NonNullable<ReturnType<typeof getHighlightForToken>>
}) {
	if (props.highlight === 'strong') {
		return <strong className="font-bold">{props.token}</strong>
	} else if (props.highlight === 'italic') {
		return <i className="italic">{props.token}</i>
	}
	return props.token
}
